import React from 'react';
import { Award, MapPin, Heart, Leaf, Trophy, Star, CheckCircle, Users } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import { localBusinessSchema, organizationSchema } from '../utils/structuredData';

const YOUTUBE_VIDEO_ID = process.env.REACT_APP_YOUTUBE_VIDEO_ID || 'j6no9DWf2AQ';

const Distilleria = () => {
  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <SEO 
        title="Distilleria | Liquorificio Lacinio - Migliore Distilleria Italiana 2025"
        description="Scopri Liquorificio Lacinio: Migliore Distilleria Italiana 2025, produttore del Miglior Amaro al Mondo. Fondata da Davide Milone e Michele Sotero nel 2019 a Crotone, Calabria. 1800 mq di eccellenza artigianale italiana."
        keywords="liquorificio lacinio, distilleria crotone, davide milone michele sotero, migliore distilleria italiana 2025, amaro calabrese premiato, produzione liquori artigianali, magna graecia, distilleria calabria"
        url="/distilleria"
        type="article"
        structuredData={[localBusinessSchema, organizationSchema]}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: 'url(/images/liquorificio.webp)' }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-amber-500/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
              <Trophy className="w-5 h-5 mr-2 text-amber-300" />
              <span className="text-amber-100 font-semibold">Migliore Distilleria Italiana 2025</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Il Liquorificio Lacinio
            </h1>
            
            <p className="text-xl md:text-2xl text-purple-100 mb-8 leading-relaxed">
              Dal cuore della Calabria nasce la distilleria premiata come <span className="font-bold text-amber-300">Migliore d'Italia 2025</span>, produttrice del <span className="font-bold text-amber-300">Miglior Amaro al Mondo</span>. Tradizione, artigianalità e innovazione racchiuse in ogni goccia.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
                <Award className="w-5 h-5 mr-2 text-amber-300" />
                <span className="text-white font-medium">Miglior Amaro al Mondo 🌍</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
                <Star className="w-5 h-5 mr-2 text-amber-300" />
                <span className="text-white font-medium">Eccellenza Calabrese 🇮🇹</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Storia Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center bg-purple-100 text-purple-600 rounded-full px-4 py-2 mb-6">
                <Heart className="w-4 h-4 mr-2" />
                <span className="font-semibold">La Nostra Storia</span>
              </div>
              
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Una Storia di Passione e Territorio
              </h2>
              
              <div className="space-y-4 text-lg text-gray-600">
                <p>
                  Il <strong>Liquorificio Lacinio</strong> è una giovane eccellenza calabrese, premiata come <strong className="text-purple-600">Migliore Distilleria Italiana 2025</strong> e riconosciuta per la produzione del <strong className="text-purple-600">Miglior Amaro al Mondo</strong>, simbolo di un artigianato autentico che unisce passione, innovazione e territorio.
                </p>
                
                <p>
                  Nata nel dicembre 2019 dalla caparbietà di due imprenditori crotonesi, <strong>Michele e Davide</strong>, l'azienda rappresenta oggi una delle realtà più dinamiche e riconosciute del panorama liquoristico italiano. La loro visione è semplice ma potente: <strong>valorizzare la Calabria</strong> attraverso prodotti che raccontano la sua bellezza incontaminata, la sua storia millenaria e i profumi del Mediterraneo.
                </p>
                
                <p>
                  La nostra è una storia umile e autentica, fatta di mani che lavorano la terra, agrumi e botaniche raccolte a mano, e di tradizioni tramandate da veri custodi del sapere artigianale che ancora oggi ispira ogni nostra creazione.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/liquorificio.webp"
                  alt="Liquorificio Lacinio"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 sm:-bottom-6 -right-2 sm:-right-6 bg-purple-600 text-white p-4 sm:p-6 rounded-xl shadow-xl max-w-[150px] sm:max-w-xs">
                <p className="text-xs sm:text-sm font-semibold mb-1">Dal</p>
                <p className="text-2xl sm:text-3xl font-bold">2019</p>
                <p className="text-xs sm:text-sm mt-2">Al servizio dell'eccellenza italiana</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-purple-100 text-purple-600 rounded-full px-4 py-2 mb-6">
              <Users className="w-4 h-4 mr-2" />
              <span className="font-semibold">I Fondatori</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Davide Milone e Michele Sotero
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Due imprenditori crotonesi uniti dalla passione per la loro terra e dalla visione di valorizzare la Calabria attraverso l'eccellenza liquoristica
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="space-y-6">
                <div className="bg-white p-8 rounded-2xl shadow-xl border-l-4 border-purple-600">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">La Visione</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Dal loro incontro nel dicembre 2019 è nata una straordinaria avventura imprenditoriale. <strong>Davide e Michele</strong> hanno trasformato un sogno in realtà, creando una distilleria che oggi è riconosciuta come la <strong className="text-purple-600">Migliore d'Italia</strong>.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-8 rounded-2xl shadow-xl text-white">
                  <p className="text-lg font-medium italic">
                    "Ogni bottiglia racconta la nostra terra, le nostre radici e l'amore per ciò che facciamo. Dalla Calabria al mondo, con orgoglio."
                  </p>
                  <p className="mt-4 text-purple-200 font-semibold">
                    — Davide & Michele
                  </p>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="/images/davide-michele-founders.webp"
                    alt="Davide Milone e Michele Sotero - Fondatori Liquorificio Lacinio"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-amber-400 rounded-full opacity-20 blur-2xl"></div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-purple-400 rounded-full opacity-20 blur-2xl"></div>
                
                {/* Info Badge */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Fondatori</p>
                      <p className="text-xl font-bold text-gray-900">Davide & Michele</p>
                    </div>
                    <div className="flex items-center bg-amber-100 text-amber-600 px-3 py-1 rounded-full">
                      <Trophy className="w-4 h-4 mr-1" />
                      <span className="text-xs font-semibold">Dal 2019</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Creatori della Migliore Distilleria Italiana 2025
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-purple-100 text-purple-600 rounded-full px-4 py-2 mb-6">
              <Leaf className="w-4 h-4 mr-2" />
              <span className="font-semibold">Scopri il Nostro Processo</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              L'Arte della Distillazione
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Un viaggio nel cuore della nostra produzione artigianale
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}`}
                title="Liquorificio Lacinio - Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Magna Graecia Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center bg-amber-100 text-amber-600 rounded-full px-4 py-2 mb-6">
              <Trophy className="w-4 h-4 mr-2" />
              <span className="font-semibold">Le Nostre Radici</span>
            </div>
            
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              L'Essenza della Magna Graecia
            </h2>
            
            <div className="text-lg text-gray-600 space-y-4 text-left">
              <p>
                Le nostre ricette nascono dalle leggende e tradizioni di <strong>Kroton</strong>, l'antica capitale della Magna Graecia, simbolo di forza, conoscenza e armonia con la natura. Ogni bottiglia è un omaggio a questo patrimonio culturale, reinterpretato con equilibrio moderno e profonda autenticità.
              </p>
              
              <p>
                I nostri liquori e amari non sono solo bevande: sono <strong className="text-purple-600">racconti liquidi</strong>, esperienze sensoriali che parlano di mare, di sole e di una Calabria che non smette mai di sorprendere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dove Siamo Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center bg-purple-100 text-purple-600 rounded-full px-4 py-2 mb-6">
                <MapPin className="w-4 h-4 mr-2" />
                <span className="font-semibold">La Nostra Sede</span>
              </div>
              
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Dove Siamo
              </h2>
              
              <p className="text-lg text-gray-600 mb-6">
                Il Liquorificio Lacinio si trova nel cuore della zona industriale di Crotone, in una struttura di proprietà di <strong>1.800 mq coperti</strong> e <strong>5.000 mq di area esterna</strong> dedicata alla produzione, al magazzino fiscale e alla ricerca di nuove ricette.
              </p>
              
              <p className="text-lg text-gray-600 mb-8">
                È qui che nascono i nostri amari pluripremiati, distillati e infusi con materie prime selezionate e lavorazioni artigianali che rispettano i tempi della natura e le tradizioni della nostra terra.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <p className="text-3xl font-bold text-purple-600 mb-2">1.800 mq</p>
                  <p className="text-sm text-gray-600">Area coperta</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <p className="text-3xl font-bold text-purple-600 mb-2">5.000 mq</p>
                  <p className="text-sm text-gray-600">Area esterna</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contatti</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Indirizzo</p>
                    <p className="text-gray-600">Zona Industriale, Crotone (KR)</p>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-500 italic">
                    "Dal cuore della Calabria, raccontiamo al mondo che anche una piccola distilleria può creare grandi emozioni."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Riconoscimenti Section */}
      <section className="py-20 bg-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              Riconoscimenti e Valori
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              Ogni goccia porta con sé la nostra filosofia: onestà, autenticità e passione per la qualità.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500 rounded-full mb-4">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Migliore Distilleria</h3>
              <p className="text-purple-200">Italiana 2025</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500 rounded-full mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Miglior Amaro</h3>
              <p className="text-purple-200">al Mondo 🌍</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-4">
                <Leaf className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Eccellenza</h3>
              <p className="text-purple-200">Artigianale Calabrese</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Made in Italy</h3>
              <p className="text-purple-200">Orgogliosamente 🇮🇹</p>
            </div>
          </div>

          <div className="mt-16 text-center">
            <p className="text-2xl font-semibold text-amber-300 italic">
              "Dal cuore della Calabria, raccontiamo al mondo che anche una piccola distilleria può creare grandi emozioni."
            </p>
          </div>
        </div>
      </section>

      {/* Media Section - Parlano di noi */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Parlano di Noi
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              L'eccellenza del Liquorificio Lacinio riconosciuta dai principali media nazionali
            </p>
          </div>

          {/* Media Logos Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-center">
            {/* RAI */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-white hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/ts6rqfsu_Logo_of_RAI_%282016%29.svg.png"
                alt="RAI"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* LA7 */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-white hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/7cd09j8v_LA7_-_Logo_2011.svg.png"
                alt="LA7"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* La Repubblica */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-white hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/s2fu7weu_La_Repubblica_logo.png"
                alt="La Repubblica"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Corriere della Sera */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-white hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/7iqsyttg_corriere.png"
                alt="Corriere della Sera"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Altri Media */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-white hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/k4kfj7d8_Screenshot%202025-10-16%20alle%2017.10.33.png"
                alt="Media"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 italic">
              "Dal cuore della Calabria, raccontiamo al mondo una storia di eccellenza e tradizione"
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Distilleria;
